import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { hmdaApi, fairLendingApi } from '../../lib/api';
import { useTeamId } from '../../hooks/useTeamId';
import toast from 'react-hot-toast';

export default function FldAnalysisModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const [selectedValidation, setSelectedValidation] = useState('');
  // Subordinate lien Underwriting and Pricing tables (Michael Wallace and Gail Berasley, 2026-10-19).
  // Automatic by default: included when subordinate lien applications are at least 33% of applications
  // (Michael, 2026-10-21). The report says which way it went and why.
  const [subordinateLien, setSubordinateLien] = useState<'auto' | 'always' | 'never'>('auto');

  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations-for-fld'],
    queryFn: async () => {
      const res = await hmdaApi.getValidations({ limit: 100 });
      return res.data;
    },
    enabled: isOpen,
  });

  const validations = validationsData?.validations || [];

  const runMutation = useMutation({
    mutationFn: () =>
      fairLendingApi.runFldAnalysis(teamId!, {
        validationId: selectedValidation,
        includeSubordinateLien: subordinateLien === 'auto' ? undefined : subordinateLien === 'always',
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['fair-lending'] });
      // Analysis runs on a background worker (P1.9); the dashboard polls until it finishes.
      toast.success('FLD Analysis queued — results will appear when it finishes');
      onClose();
      if (onSuccess && res.data?.analysis?.id) {
        onSuccess(res.data.analysis.id);
      }
    },
    onError: () => toast.error('FLD Analysis failed to queue. Please try again.'),
  });

  const canSubmit = selectedValidation && !runMutation.isPending;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-panel rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-ink">
                    Run Fair Lending Dashboard (FLD)
                  </Dialog.Title>
                  <button onClick={onClose} className="text-ink-4 hover:text-ink-3">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-sm text-teal-800">
                    <p className="font-medium">IFLEP Analysis — Full Fair Lending Dashboard</p>
                    <p className="mt-1 text-teal-600 text-xs">
                      Runs all 17 risk factors across 9 prohibited basis groups and 6 loan purposes.
                      Includes underwriting, pricing, steering, redlining, and marketing analyses
                      with chi-square, Fisher's exact, and Student's t-test statistical testing.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-2 mb-1">
                      HMDA Validation Dataset
                    </label>
                    <select
                      value={selectedValidation}
                      onChange={(e) => setSelectedValidation(e.target.value)}
                      className="block w-full rounded-md border-rule text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    >
                      <option value="">Select a validation...</option>
                      {validations.map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {v.fileName} — {v.totalLoans} loans ({v.institutionName || 'Unknown'})
                        </option>
                      ))}
                    </select>
                    {validations.length === 0 && (
                      <p className="mt-1 text-xs text-ink-4">
                        No HMDA validations found. Upload and validate a LAR file first.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-2 mb-1">Subordinate lien tables</label>
                    <select
                      value={subordinateLien}
                      onChange={(e) => setSubordinateLien(e.target.value as 'auto' | 'always' | 'never')}
                      className="block w-full rounded-md border-rule text-sm shadow-sm focus:border-teal-500 focus:ring-teal-500"
                    >
                      <option value="auto">Automatic (when 33% or more of applications are subordinate lien)</option>
                      <option value="always">Always include</option>
                      <option value="never">Don&apos;t include</option>
                    </select>
                    <p className="mt-1 text-xs text-ink-4">
                      Adds each Underwriting and Pricing table on Conventional / Subordinate Lien / Principal
                      Residence / Site-Built / Not Commercial (CSLPRSBnotComml), right after its first lien table.
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="rounded-md px-4 py-2 text-sm font-medium text-ink-2 hover:bg-panel-2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => runMutation.mutate()}
                    disabled={!canSubmit}
                    className="rounded-md px-4 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {runMutation.isPending ? 'Running FLD Analysis...' : 'Run FLD Analysis'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
